<cs-header>
  <cs-header-title>{{ LL.labelScanner.title() }}</cs-header-title>
</cs-header>

<cs-content>
  <!-- Camera Permission Denied -->
  @if (cameraPermissionDismissed) {
    <cs-scanner-permission-denied [message]="LL.labelScanner.cameraPermissionDenied()" [scanner]="ScannerName.Label"></cs-scanner-permission-denied>
  }

  @if (!cameraPermissionDismissed && !resultGroups) {
    <cs-scanner-camera-stage #stage
      [photos]="capturedPhotos"
      [aspectRatio]="'1:1'"
      [analyzing]="analyzing"
      [progress]="analyzeProgress"
      [statusText]="analyzeStatusText"
      [retryMessage]="retryMessage"
      [suppressAnalyzingOverlay]="true"
      (cameraReady)="onCameraStateChange()"
      (cameraError)="onCameraError($event)"
      (permissionChange)="onCameraStateChange()">
    </cs-scanner-camera-stage>

    <!-- the analyzing progress sits in a reserved bottom bar (below the photo),
         matching the meal scanner, instead of overlaying the photo -->
    @if (analyzing) {
      <div class="scanner-bottom-bar">
        <cs-scanner-analyzing-overlay
          [statusText]="analyzeStatusText"
          [progress]="analyzeProgress"
          [retryMessage]="retryMessage">
        </cs-scanner-analyzing-overlay>
      </div>
    }

    <!-- Bottom section: reel + hints + shutter bar (hidden during analysis and when all photos taken) -->
    @if (!analyzing && !allPhotosTaken) {
      <cs-scanner-photo-reel #reel
        [photos]="capturedPhotos"
        [maxSlots]="MAX_PHOTOS"
        [canAdd]="!stage.cameraView?.isActive"
        (remove)="removePhotoAt($event)"
        (inspect)="inspectPhotoAt($event)"
        (add)="addPhoto()">
      </cs-scanner-photo-reel>

      <cs-scanner-hints [hints]="photoHints"></cs-scanner-hints>

      <!-- Shutter bar: upload + model selector left, shutter center, Done right -->
      <cs-scanner-shutter-bar
        [shutterHidden]="!stage.cameraView?.isActive"
        [nativeShutterSound]="stage.cameraView?.isNative"
        (snap)="snapPhoto()">
        <div left class="left-buttons">
          <cs-button icon="add" secondary (click)="openFilePicker()"></cs-button>
          @if (modelSelection.scanMode === 'any') {
            <cs-scanner-model-picker
              [modelOptions]="modelSelection.modelOptions"
              [selectedModel]="modelSelection.selectedModel"
              (modelSelected)="onModelSelected($event)">
            </cs-scanner-model-picker>
          }
        </div>
        <div right>
          @if (hasPhotos && !errorAlertOpen) {
            <cs-button primary small [label]="LL.labelScanner.doneButton()" (click)="analyzeLabel()"></cs-button>
          }
        </div>
      </cs-scanner-shutter-bar>
    }
  }

  <!-- Comparison UI ('all' mode, when models disagree) -->
  @if (resultGroups && resultGroups.length > 1) {
    <cs-stepper [steps]="stepperSteps" [style]="StepperStyle.Traditional" (stepClick)="onGroupStepClick($event)">
    </cs-stepper>

    <cs-card-list>
      <cs-card>
        <div class="group-header">
          <span class="group-models">{{ resultGroups[activeGroupIndex].displayNames.join(', ') }}</span>
          <span class="group-count">{{ LL.labelScanner.modelsAgree({ count: resultGroups[activeGroupIndex].models.length })
            }}</span>
        </div>
        <div class="group-meta">
          <div class="meta-row">{{ LL.labelScanner.labelType() }}: {{ resultGroups[activeGroupIndex].labelStyle || '?' }}
          </div>
          @for (c of resultGroups[activeGroupIndex].confidences; track c.model) {
          <div class="meta-row">{{ LL.labelScanner.confidence() }}: {{ c.model }} {{ confidencePctLabel(c.confidence) }}%</div>
          }
          @if (resultGroups[activeGroupIndex].jsonRepaired) {
          <div class="meta-row warning">{{ LL.labelScanner.jsonRepaired() }}</div>
          }
        </div>
        @if (resultGroups[activeGroupIndex].measures?.length) {
        <div class="group-measures">
          @for (m of resultGroups[activeGroupIndex].measures; track m.name) {
          <span>{{ m.amount + ' ' + m.name + ' (' + m.weight_g + 'g)' }}</span>
          }
        </div>
        }
        <div class="nutrient-list">
          @for (entry of getNutrientEntries(resultGroups[activeGroupIndex].nutrients); track entry.key) {
          <div class="nutrient-row"
            [class.agree-majority]="entry.agreeCount >= totalModelsInComparison * 0.5 && entry.agreeCount < totalModelsInComparison"
            [class.disagree]="entry.agreeCount < totalModelsInComparison * 0.5">
            <span class="nutrient-key">{{ entry.label }}</span>
            <span class="nutrient-value">{{ nutrientValueLabel(entry.value) }}</span>
            @if (entry.confidence != null) {
            <span class="nutrient-conf" [class.low]="entry.confidence < 0.85">{{ confidencePctLabel(entry.confidence) }}%</span>
            }
            @if (entry.agreeCount < totalModelsInComparison) { <span class="nutrient-agree">{{ entry.agreeCount }}/{{
              totalModelsInComparison }}</span>
              }
          </div>
          }
        </div>
      </cs-card>
    </cs-card-list>
  }
</cs-content>

<!-- Done + Retake buttons (when all photos taken, before analysis), stacked vertically -->
@if (allPhotosTaken && !analyzing && !resultGroups && !errorAlertOpen) {
  <cs-page-action-buttons class="vertical">
    <cs-button primary [label]="LL.labelScanner.doneButton()" (click)="analyzeLabel()"></cs-button>
    <cs-button secondary [label]="LL.labelScanner.retakeButton()" (click)="retakeAllPhotos()"></cs-button>
  </cs-page-action-buttons>
}

<!-- Accept + Retake buttons (comparison review), stacked vertically -->
@if (resultGroups && resultGroups.length > 1) {
  <cs-page-action-buttons class="vertical">
    <cs-button primary [label]="LL.labelScanner.acceptResult()" (click)="acceptGroup(resultGroups[activeGroupIndex])"></cs-button>
    <cs-button secondary [label]="LL.labelScanner.retakeButton()" (click)="retakeAllPhotos()"></cs-button>
  </cs-page-action-buttons>
}
